import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import { useCache } from '../../context/CacheContext';
import { cachedPayerService } from '../../services/cachedApiService';
import { PriorAuthorization } from '../../types';
import '../../styles/ReviewAuthorization.css';

const ReviewedAuthorizations: React.FC = () => {
  const [authorizations, setAuthorizations] = useState<PriorAuthorization[]>([]);
  const [filteredAuthorizations, setFilteredAuthorizations] = useState<PriorAuthorization[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'approved' | 'rejected'>('all');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const { invalidate } = useCache();

  useEffect(() => {
    const fetchData = async () => {
      if (!user || user.role !== 'payer') {
        setError('Only payers can access this page');
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        
        // Fetch reviewed authorizations for this payer
        const reviewedAuths = await cachedPayerService.getReviewedAuthorizations();
        
        setAuthorizations(reviewedAuths);
        setFilteredAuthorizations(reviewedAuths);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching reviewed authorizations:', err);
        setError('Failed to fetch reviewed authorizations');
        setLoading(false);
      }
    };
    
    fetchData();
  }, [user]);

  // Apply filters (search term and status)
  useEffect(() => {
    let filtered = authorizations;
    
    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(auth => auth.status === statusFilter);
    }
    
    // Apply search filter
    if (searchTerm.trim() !== '') {
      filtered = filtered.filter(auth =>
        auth.patient.toLowerCase().includes(searchTerm.toLowerCase()) ||
        auth.request_type.toLowerCase().includes(searchTerm.toLowerCase()) ||
        auth.urgency.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (auth.clinical_reason && auth.clinical_reason.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (auth.rejection_reason && auth.rejection_reason.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    setFilteredAuthorizations(filtered);
  }, [searchTerm, statusFilter, authorizations]);

  if (loading) {
    return (
      <Layout>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <div className="loading-text">Loading reviewed authorizations...</div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="error-container">
          <div className="message-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M12 9V13M12 17H12.01M5.07183 19H18.9282C20.4678 19 21.4301 17.3333 20.6603 16L13.7321 4C12.9623 2.66667 11.0378 2.66667 10.268 4L3.33978 16C2.56998 17.3333 3.53223 19 5.07183 19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="message-text">{error}</div>
          <div className="message-actions" style={{ marginTop: '15px' }}>
            <button
              onClick={() => {
                invalidate.priorAuths();
                window.location.reload();
              }}
              className="retry-button"
              style={{
                padding: '8px 16px',
                backgroundColor: '#3182ce',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Retry
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="reviewed-auth-container">
        <h2 className="review-auth-title">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
            <path d="M14 3V7C14 7.55 14.45 8 15 8H19" stroke="currentColor" strokeWidth="2" />
            <path d="M17 21H7C5.90 21 5 20.10 5 19V5C5 3.89 5.90 3 7 3H14L19 8V19C19 20.10 18.10 21 17 21Z" stroke="currentColor" strokeWidth="2" />
            <path d="M9 15L11 17L15 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Reviewed Authorizations
        </h2>

        {/* Filter Controls */}
        <div className="filter-controls">
          <div className="filter-buttons">
            <button
              type="button"
              className={`filter-btn all ${statusFilter === 'all' ? 'active' : ''}`}
              onClick={() => setStatusFilter('all')}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M4 6H20M4 12H20M4 18H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              All
            </button>
            <button
              type="button"
              className={`filter-btn approved ${statusFilter === 'approved' ? 'active' : ''}`}
              onClick={() => setStatusFilter('approved')}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M5 13L9 17L19 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Approved
            </button>
            <button
              type="button"
              className={`filter-btn rejected ${statusFilter === 'rejected' ? 'active' : ''}`}
              onClick={() => setStatusFilter('rejected')}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M6 18L18 6M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Rejected
            </button>
          </div>
          
          <div className="auth-count">
            {filteredAuthorizations.length} {filteredAuthorizations.length === 1 ? 'authorization' : 'authorizations'} found
          </div>
        </div>

        {/* Search Bar */}
        <div className="search-container">
          <div className="search-input-wrapper">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="search-icon">
              <path d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <input
              type="text"
              placeholder="Search by patient ID, type, or reason..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            {searchTerm && (
              <button
                className="search-clear-btn"
                onClick={() => setSearchTerm('')}
                aria-label="Clear search"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            )}
          </div>
        </div>

        <ul className="auth-list">
          {filteredAuthorizations.length > 0 ? (
            filteredAuthorizations.map((auth) => (
              <li
                key={auth.id}
                className={`auth-item ${auth.status === 'approved' ? 'approved' : auth.status === 'rejected' ? 'rejected' : ''}`}
              >
                <div className="auth-item-left">
                  <div className={`auth-avatar ${auth.request_type}`}>
                    {auth.request_type === 'prescription' ? 'Rx' : 'Pr'}
                  </div>
                  <div className="auth-info">
                    <div className="auth-patient-id">
                      Patient ID: {auth.patient}
                    </div>
                    <div className="auth-meta">
                      <span className="auth-type">
                        {auth.request_type.charAt(0).toUpperCase() + auth.request_type.slice(1)}
                      </span>
                      <span>•</span>
                      <span className="auth-date">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                          <path d="M8 7V3M16 7V3M7 11H17M5 21H19C20.1046 21 21 20.1046 21 19V7C21 5.89543 20.1046 5 19 5H5C3.89543 5 3 5.89543 3 7V19C3 20.1046 3.89543 21 5 21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        {auth.review_date?.split(' ')[0].slice(0,10) || 'Unknown'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="auth-item-right">
                  <span className={`status-badge ${auth.status === 'approved' ? 'approved' : 'rejected'}`}>
                    {auth.status === 'approved' ? (
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                        <path d="M5 13L9 17L19 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    ) : (
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                        <path d="M6 18L18 6M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                    {auth.status.charAt(0).toUpperCase() + auth.status.slice(1)}
                  </span>
                  
                  <span className={`badge ${auth.urgency === 'urgent' ? 'warning' : 'normal'}`}>
                    {auth.urgency === 'urgent' ? (
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                        <path d="M12 9V13M12 17H12.01M5.1 19H18.9C20.5 19 21.4 17.33 20.7 16L13.7 4C13 2.67 11 2.67 10.3 4L3.3 16C2.6 17.33 3.5 19 5.1 19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    ) : (
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                        <path d="M12 16V12M12 8H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                    {auth.urgency.charAt(0).toUpperCase() + auth.urgency.slice(1)}
                  </span>
                  
                  {auth.status === 'rejected' && auth.rejection_reason && (
                    <span className="reason-badge" title={auth.rejection_reason}>
                      Reason: {auth.rejection_reason}
                    </span>
                  )}
                  
                  {auth.clinical_reason && (
                    <button
                      className="details-btn"
                      title={auth.clinical_reason}
                      onClick={() => alert(`Clinical Reason: ${auth.clinical_reason}`)}
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                        <path d="M13 16H12V12H11M12 8H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                      Details
                    </button>
                  )}
                </div>
              </li>
            ))
          ) : (
            <li className="empty-state">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                <path d="M14 3V7C14 7.55 14.45 8 15 8H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M17 21H7C5.90 21 5 20.10 5 19V5C5 3.89 5.90 3 7 3H14L19 8V19C19 20.10 18.10 21 17 21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M9 15L11 17L15 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <div className="empty-state-title">
                {authorizations.length > 0
                  ? `No authorizations found matching your filters`
                  : "No reviewed authorizations found"}
              </div>
              <div className="empty-state-subtitle">
                {authorizations.length > 0
                  ? "Try different search terms or filters"
                  : "Review some authorizations to see them here"}
              </div>
              {authorizations.length > 0 && (searchTerm || statusFilter !== 'all') && (
                <button
                  className="clear-filters-btn"
                  onClick={() => {
                    setSearchTerm('');
                    setStatusFilter('all');
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" fill="currentColor"/>
                  </svg>
                  Clear filters
                </button>
              )}
            </li>
          )}
        </ul>
      </div>
    </Layout>
  );
};

export default ReviewedAuthorizations;